import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _uniq from "lodash/uniq";
import _flatten from "lodash/flatten";
var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
import { action, computed, observable } from 'mobx';
import { Collection } from 'core/model/Collection';
import api from 'core/util/api';
import DeviceModel from './DeviceModel';
import $setup from '../$setup';
const SEARCHABLE_FIELDS = ['agentName', 'appliedLabels', 'device_manufacturer', 'device_name', 'deviceType', 'flowSource', 'flowType', 'id', 'ip_address', 'model', 'os_name', 'os_version', 'plan_name', 'sending_ips', 'serial_number', 'site_market_name', 'site_name', 'vendor'];
let DeviceCollection = (_dec = observable.ref, _dec2 = observable.ref, _dec3 = observable.ref, _dec4 = observable.ref, _class = class DeviceCollection extends Collection {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "memoryUtilQueryData", _descriptor, this);
    _initializerDefineProperty(this, "cpuUtilQueryData", _descriptor2, this);
    _initializerDefineProperty(this, "temperatureQueryData", _descriptor3, this);
    _initializerDefineProperty(this, "trafficQueryData", _descriptor4, this);
    this.useAsyncAdd = true;
    this.setMemoryUtilQueryData = () => {
      this.memoryUtilQueryData.forEach(result => {
        const device = this.get(result.device_id);
        if (device && device.get('memory_util') !== result.last_memory_util) {
          device.set('memory_util', result.last_memory_util);
        }
        if (device && device.get('memory_util_timeseries') !== result.sparklineData) {
          device.set('memory_util_timeseries', result.sparklineData);
        }
      });
    };
    this.setCpuUtilQueryData = () => {
      this.cpuUtilQueryData.forEach(result => {
        const device = this.get(result.device_id);
        if (device && device.get('cpu_util') !== result['last_total/instant']) {
          device.set('cpu_util', result['last_total/instant']);
        }
        if (device && device.get('cpu_util_timeseries') !== result.sparklineData) {
          device.set('cpu_util_timeseries', result.sparklineData);
        }
      });
    };
    this.setTemperatureQueryData = () => {
      this.temperatureQueryData.forEach(result => {
        const device = this.get(result.device_id);
        if (device) {
          device.set('temperature', {
            avg_instant: result.avg_instant,
            max_instant: result.max_instant,
            last_instant: result.last_instant
          });
        }
        if (device && device.get('temperature_timeseries') !== result.sparklineData) {
          device.set('temperature_timeseries', result.sparklineData);
        }
      });
    };
    this.setTrafficQueryData = () => {
      const {
        deviceNameIdHash
      } = this;
      this.trafficQueryData.forEach(result => {
        const id = deviceNameIdHash[result.i_device_name];
        const device = id ? this.get(id) : null;
        if (device && device.get('traffic') !== result.f_sum_both_bytes__k_last) {
          device.set('traffic', result.f_sum_both_bytes__k_last);
        }
        if (device && device.get('traffic_timeseries') !== result.sparklineData) {
          device.set('traffic_timeseries', result.sparklineData);
        }
      });
    };
  }
  get defaultSortState() {
    return {
      field: 'device_name',
      direction: 'asc'
    };
  }
  get secondarySort() {
    return this.defaultSortState;
  }
  get url() {
    return '/api/ui/devices';
  }
  get model() {
    return DeviceModel;
  }
  get presetGroups() {
    return [{
      name: 'site_name',
      label: 'Site',
      sortBy: 'site_name',
      icon: 'map-marker'
    }, {
      name: 'plan_id',
      label: 'Plan',
      icon: 'card_membership',
      materialIcon: true
    }, {
      name: 'deviceType',
      label: 'Type',
      icon: 'dev-router-plain'
    }, {
      name: 'appliedLabels',
      label: 'Label',
      sortBy: 'appliedLabels',
      icon: 'tag'
    }];
  }
  getFilterValues(model) {
    return SEARCHABLE_FIELDS.map(name => model[name] ?? model.get(name)).filter(value => value != null && value !== '');
  }
  get percentUnlabeled() {
    return this.isRequestActive('fetching') ? 0 : Math.floor(this.unlabeledDevices.length / this.size * 100);
  }
  get unlabeledDevices() {
    return this.models.filter(device => !device.hasLabels);
  }
  get deviceIdNameHash() {
    const hash = {};
    this.models.forEach(device => {
      hash[device.id] = device.get('device_name');
    });
    return hash;
  }
  get deviceNameIdHash() {
    const hash = {};
    this.unfiltered.forEach(device => {
      hash[device.get('device_name')] = device.id;
    });
    return hash;
  }

  // returns a unique array of label names for the selected models
  get selectedLabelNames() {
    if (!this.selectedSize) {
      return [];
    }
    const labels = _flatten(this.selectedArray.map(m => m.get('labels').map(l => l.name)));
    return _uniq(labels);
  }
  getLinkedDeviceModel(model) {
    if (model.get('device_bgp_type') === 'other_device') {
      return this.models.find(item => item.get('id') === model.get('use_bgp_device_id')?.toString());
    }
    return null;
  }
  async bulkDelete(options = {}) {
    const {
      selectedArray
    } = this;
    return super.bulkDelete({
      ...options,
      removeFromCollection: false
    }).then(result => {
      selectedArray?.filter(device => !result.hasErrors || !result.errors?.find(errDevice => errDevice.deviceId === device.id)).forEach(model => {
        if (model.isArchived) {
          this.remove(model.id);
        } else {
          model.set('device_status', 'D');
        }
      });
      return result;
    });
  }
  async bulkUpdateSite(url, data) {
    this.setRequestStatus('updating');
    try {
      const {
        ids,
        name: site_name,
        site_market_id,
        site_market_name
      } = await api.post(`${url}/bulkUpdateSite`, {
        data
      });
      // update the site for the devices which were ACTUALLY updated.
      // note that this could be different from the devices which were requested to update due to RBAC permissions
      // TODO we should probably have better UX for this but for now we'll consider it a corner case
      ids.forEach(id => {
        this.get(id)?.safeSet({
          site_id: data.value,
          site_market_id,
          site_market_name,
          site_name
        });
      });
    } finally {
      this.setRequestStatus(null);
    }
  }
  async bulkUpdatePlan(url, data) {
    this.setRequestStatus('updating');
    try {
      const {
        ids,
        plan_name
      } = await api.post(`${url}/bulkUpdatePlan`, {
        data
      });
      // update the plan for the devices which were ACTUALLY updated.
      // note that this could be different from the devices which were requested to update due to RBAC permissions
      // TODO we should probably have better UX for this but for now we'll consider it a corner case

      // we will dynamically update plan device counts in order to avoid fetching this entire table
      const decrements = {};
      ids.forEach(id => {
        const prevPlan = this.get(id)?.get('plan_id');
        decrements[prevPlan] = (decrements[prevPlan] || 0) + 1;
        this.get(id)?.safeSet({
          plan_id: data.value,
          plan_name
        });
      });

      // update $setup plans collection with new device counts
      $setup.plans = $setup.plans.map(plan => {
        if (String(plan.id) === String(data.value)) {
          plan.device_count += ids.length;
        }
        if (decrements[plan.id]) {
          plan.device_count -= decrements[plan.id];
        }
        return plan;
      });
    } finally {
      this.setRequestStatus(null);
    }
  }
  async bulkUpdate(name, value) {
    if (name === 'kentik_agent_snmp_options.agent_id') {
      return super.bulkUpdate(name, value);
    }
    const {
      url
    } = this;
    const {
      selectedIds
    } = this;
    if (!selectedIds?.length || typeof value !== 'string' && typeof value !== 'number') {
      return null;
    }
    const data = {
      ids: selectedIds,
      value
    };
    return name === 'site_id' ? this.bulkUpdateSite(url, data) : this.bulkUpdatePlan(url, data);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "memoryUtilQueryData", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "cpuUtilQueryData", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "temperatureQueryData", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "trafficQueryData", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _applyDecoratedDescriptor(_class.prototype, "percentUnlabeled", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "percentUnlabeled"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unlabeledDevices", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "unlabeledDevices"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deviceIdNameHash", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "deviceIdNameHash"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deviceNameIdHash", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "deviceNameIdHash"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectedLabelNames", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "selectedLabelNames"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "bulkUpdatePlan", [action], Object.getOwnPropertyDescriptor(_class.prototype, "bulkUpdatePlan"), _class.prototype), _class);
export default DeviceCollection;